import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Switch, Paper, Link } from '@mui/material';
import { Button, Card, Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { emptyArray, emptyString } from 'constants/emptyConst';
import { addressFieldMaxLength } from 'constants/numericLimits';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import insuranceDurations from 'constants/insuranceDurations';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { CATEGORY_SUBCATEGORY_USAGE_TYPE_AND_ACTIVITY_FIELD } from '../../../queries';
import {
  atLeastOne,
  fromModel,
  getErrors,
  isValid,
  required,
  shape,
  stopOnFirstFailure,
  when,
  maxLength
} from '@totalsoft/pure-validations';
import { partnerType } from 'constants/partnerType';
import * as R from 'ramda';
import { getTranslationByCount } from 'constants/common';
import { loadCities, loadCountries, loadCounties } from 'features/offer/edit/client/form/actionCreators';
import { env } from 'utils/env';
import { RightsContext } from 'providers/RightsContext';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isDirty } from '@totalsoft/change-tracking';
import debounce from 'debounce-promise';
import { isValidDateFormat } from 'utils/pure-validation/primitiveValidators';
import { useValidation } from '@totalsoft/pure-validations-react';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import { Prompt } from 'react-router-dom';
import useInsuranceParams from '../../../useInsuranceParams';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useMutation } from '@apollo/client';
import { SAVE_INSURANCE_SIMULATION, SELECT_DEFAULT_SUBCATEGORY } from '../../../mutations';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import { INSURANCE_FINANCING_OFFER_SAVED_EVENT, INSURANCE_QUOTATION_SELECTED_EVENT, QUOTATIONS_COMPUTED_EVENT } from '../../../eventTypes';
import {
  DEFAULT_SUBCATEGORY_SELECTED,
  INSURANCE_FINANCING_OFFER_SAVED,
  INSURANCE_QUOTATIONS_STATUS_CHANGED,
  QUOTATIONS_COMPUTED,
  RCA_QUOTATION_SELECTED
} from '../../../subscriptions';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { rcaSelectedQuotationVar, rcaSelectionInProgressVar } from '../../../variables';

const validator = fromModel(insured =>
  shape({
    usageTypeCode: required,
    countyId: required,
    cityId: required,
    street: maxLength(addressFieldMaxLength['street']),
    streetNumber: maxLength(addressFieldMaxLength['number']),
    zipCode: maxLength(addressFieldMaxLength['zipCode']),
    drivingLicenseDate:
      [required |> when(R.any(x => x === insured.partnerTypeCode)([partnerType.PF, partnerType.PFA])), isValidDateFormat]
      |> stopOnFirstFailure,
    defaultActivityFieldId: required |> when(R.any(x => x === insured.partnerTypeCode)([partnerType.PJ, partnerType.PFA])),
    categoryCode: required,
    subcategoryCode: required,
    insuranceSimulationXPeriod: atLeastOne,
    insuredName: required
  })
);

const RCAInsuranceGeneralData = ({
  data,
  refetchSimulation,
  refetchQuotations,
  actions,
  counties,
  config,
  documentDetail,
  saving,
  setSaving,
  isReadOnly
}) => {
  const { t } = useTranslation();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const loadCitiesDebounced = useRef(debounce(actions.loadCities, 500));
  const { isImage } = useContext(RightsContext);
  const { menuInsurance: hasRightToOpenInsurance } = useContext(GlobalRightsContext);
  const { offerId, offerSiteId } = useInsuranceParams();
  const [validation, validate] = useValidation(validator);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();

  // #region Memos

  const insuranceSimulation = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { __typename, ...rest } = data?.insuranceSimulation || {};
    return rest;
  }, [data]);

  const insuranceTypeCode = useMemo(() => lens.insuranceTypeCode |> get, [lens]);

  const itemTypeCode = useMemo(() => lens.itemTypeCode |> get, [lens]);

  const isLensDirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  const shouldShowFinancedInsuranceInfo = false;

  const county = useMemo(() => {
    const id = lens.countyId |> get;
    if (!id) return null;
    return {
      id,
      name: lens.countyName |> get
    };
  }, [lens]);

  const city = useMemo(() => {
    const id = lens.cityId |> get;
    if (!id) return null;
    return {
      id,
      name: lens.cityName |> get
    };
  }, [lens]);

  // #endregion

  const { data: auxiliaryData, loading } = useQueryWithErrorHandling(CATEGORY_SUBCATEGORY_USAGE_TYPE_AND_ACTIVITY_FIELD, {
    variables: {
      insuranceTypeCode,
      itemTypeCode
    },
    skip: !insuranceTypeCode || !itemTypeCode
  });

  const subcategoriesByCategoryCode = useMemo(() => {
    if (loading) return emptyArray;

    if ((lens.categoryCode |> get) === null) return emptyArray;

    return auxiliaryData?.assetSubcategories.filter(subcategory => subcategory.categoryCode === (lens.categoryCode |> get));
  }, [auxiliaryData, lens, loading]);

  const internalLoadCities = useCallback(
    input => {
      if (!input) {
        return Promise.resolve([]);
      }
      return loadCitiesDebounced.current(input, lens.countyId |> get).then(data => {
        return data;
      });
    },
    [lens]
  );

  const handleInsurancePeriodChange = useCallback(
    value => {
      const rcaInsurance = lens |> get;
      const remainingPeriods = rcaInsurance.insuranceSimulationXPeriod.filter(x => value.find(p => p === x.period));
      const addedPeriods = value
        .filter(x => !remainingPeriods.find(p => p.period === x))
        .map(x => ({ insuranceSimulationId: rcaInsurance.id, period: x }));
      const newPeriods = remainingPeriods.concat(addedPeriods);
      (lens.insuranceSimulationXPeriod |> set)(newPeriods);
    },
    [lens]
  );

  const activityFields = useMemo(() => {
    if (loading || !auxiliaryData?.activityFields) return emptyArray;

    return auxiliaryData?.activityFields.map(x => ({
      id: x.id,
      code: x.code,
      name: x.name,
      shownValue: `${x.code} / ${x.name}`
    }));
  }, [auxiliaryData, loading]);

  const getLimitTagsText = useCallback((more, singularTag, pluralTag) => getTranslationByCount(more, singularTag, pluralTag, t), [t]);

  const insuranceOfferURL = useMemo(() => {
    if ((lens?.externalInsuranceDocumentId |> get) !== null && (lens?.externalInsuranceDocumentNumber |> get) !== null) {
      return `${env.REACT_APP_LSNG_INSURANCE_URL}/insurance-documents/${lens.externalInsuranceDocumentId |> get}`;
    }
  }, [lens]);

  // #region Side effects

  useEffect(() => {
    actions.loadCountries();
    actions.loadCounties('', config.defaultCountry.id);
  }, [actions, config]);

  useEffect(() => {
    const current = lens |> get;
    if (current && current.existsSocialAddress !== Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress)) {
      resetLens({
        ...current,
        existsSocialAddress: Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress)
      });
      return;
    }
  }, [data?.leasingSimulationByOffer?.insured?.socialAddress, lens, resetLens]);

  useEffect(() => {
    if (!insuranceSimulation || isLensDirty) return;

    const insured = data?.leasingSimulationByOffer?.insured;

    resetLens({
      ...insuranceSimulation,
      insuredName: insured?.name,
      countyId: insured?.socialAddress?.districtId,
      countyName: insured?.socialAddress?.districtName,
      cityId: insured?.socialAddress?.cityId,
      cityName: insured?.socialAddress?.cityName,
      street: insured?.socialAddress?.street,
      streetNumber: insured?.socialAddress?.streetNumber,
      zipCode: insured?.socialAddress?.zipCode,
      existsSocialAddress: Boolean(insured?.socialAddress),
      drivingLicenseDate: insured?.drivingLicenseDate,
      defaultActivityFieldId: insured?.defaultActivityFieldId,
      defaultActivityFieldCode: insured?.defaultActivityFieldCode,
      defaultActivityFieldName: insured?.defaultActivityFieldName,
      existsCaen: Boolean(insured?.defaultActivityFieldId),
      financedInsurance: Boolean(insuranceSimulation?.financedInsurance),
      itemTypeCode: documentDetail.item.typeCode,
      partnerTypeCode: insured?.partnerTypeCode
    });
  }, [insuranceSimulation, resetLens, documentDetail.item.typeCode, isLensDirty, data?.leasingSimulationByOffer?.insured]);

  // #endregion

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      if (!(lens.existsSocialAddress |> get)) refetchSimulation();
      setSaving(false);
      onEndCall();
    }
  });

  // #region Save simulation

  const [saveInsuranceSimulation] = useMutation(SAVE_INSURANCE_SIMULATION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
      onEndCall();
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.saveInsuranceSimulation;
      storeKey({ correlationId });
    }
  });

  const handleSaveInsuranceSimulation = useCallback(() => {
    if (!validate(lens |> get)) {
      return;
    }

    setSaving(true);
    onBeginCall();

    saveInsuranceSimulation({
      variables: {
        insuranceSimulation: {
          id: lens.id |> get,
          documentDetailId: documentDetail.documentDetailId,
          documentDetailSiteId: documentDetail.siteId,
          assetInformationId: documentDetail.assetInformationId,
          offerId,
          offerSiteId,
          countyId: lens.countyId |> get,
          cityId: lens.cityId |> get,
          street: lens.street |> get,
          streetNumber: lens.streetNumber |> get,
          zipCode: lens.zipCode |> get,
          drivingLicenseDate:
            (lens.drivingLicenseDate |> get) instanceof Date
              ? (lens.drivingLicenseDate |> get).toDateString()
              : lens.drivingLicenseDate |> get,
          defaultActivityFieldId: lens.defaultActivityFieldId |> get,
          categoryCode: lens.categoryCode |> get,
          subcategoryCode: lens?.subcategoryCode |> get,
          insuranceTypeCode: insuranceSimulation.insuranceTypeCode,
          insuranceSimulationXPeriod: (lens.insuranceSimulationXPeriod |> get).map(x => ({
            insuranceSimulationId: x.insuranceSimulationId,
            period: x.period
          })),
          usageTypeCode: lens.usageTypeCode |> get,
          financedInsurance: lens.financedInsurance |> get,
          allowRtiOption: false,
          financedInsurancePeriod: (lens.financedInsurancePeriod |> get) === emptyString ? null : lens.financedInsurancePeriod |> get
        }
      }
    });
  }, [onBeginCall, documentDetail, lens, offerId, offerSiteId, saveInsuranceSimulation, setSaving, insuranceSimulation, validate]);

  // #endregion

  // #region Update category

  const [selectDefaultSubcategory] = useMutation(SELECT_DEFAULT_SUBCATEGORY, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
    },
    onCompleted: data => {
      const { correlationId } = data.selectDefaultSubcategory;
      storeKey({ correlationId });
    }
  });

  const handleChangeCategory = useCallback(
    value => {
      (lens.categoryCode |> set)(value);
      if (value)
        selectDefaultSubcategory({
          variables: {
            offerId,
            offerSiteId,
            insuranceSimulationId: insuranceSimulation.id,
            documentDetailId: documentDetail.documentDetailId,
            categoryCode: value
          }
        });
    },
    [documentDetail, lens, offerId, offerSiteId, selectDefaultSubcategory, insuranceSimulation]
  );

  // #endregion

  // #region Subscriptions

  useGlobalSubscription(QUOTATIONS_COMPUTED, {
    variables: { insuranceSimulationId: insuranceSimulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { quotationsComputed }
      }
    }) => {
      if (quotationsComputed) {
        setSaving(false);
        onEndCall();
        addToast(t('Notifications.' + QUOTATIONS_COMPUTED_EVENT), 'info');
        resetLens(prev => ({
          ...prev,
          externalInsuranceDocumentId: insuranceSimulation?.externalInsuranceDocumentId,
          externalInsuranceDocumentNumber: insuranceSimulation?.externalInsuranceDocumentNumber
        }));
      }
    }
  });

  useGlobalSubscription(INSURANCE_FINANCING_OFFER_SAVED, {
    variables: {
      insuranceSimulationId: insuranceSimulation.id,
      insuranceTypeCode: insuranceSimulation.insuranceTypeCode
    },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceFinancingOfferSaved }
      }
    }) => {
      if (!insuranceFinancingOfferSaved) return;

      if (!insuranceFinancingOfferSaved?.quotationsInvalidated) {
        setSaving(false);
        onEndCall();
        addToast(
          t('Notifications.' + INSURANCE_FINANCING_OFFER_SAVED_EVENT, {
            data: { insuranceTypeName: insuranceSimulation.insuranceTypeName }
          }),
          'info'
        );
      } else refetchQuotations();

      resetLens();
      refetchSimulation();
    }
  });

  useGlobalSubscription(INSURANCE_QUOTATIONS_STATUS_CHANGED, {
    variables: { insuranceSimulationId: insuranceSimulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceQuotationsStatusChanged }
      }
    }) => {
      const { quotationsStatus } = insuranceQuotationsStatusChanged;
      resetLens(prev => ({
        ...prev,
        quotationsStatus
      }));
    }
  });

  useGlobalSubscription(DEFAULT_SUBCATEGORY_SELECTED, {
    variables: { insuranceSimulationId: insuranceSimulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { defaultSubcategorySelected }
      }
    }) => {
      const { defaultSubcategoryCode: subcategoryCode } = defaultSubcategorySelected;
      (lens.subcategoryCode |> set)(subcategoryCode);
    }
  });

  useGlobalSubscription(RCA_QUOTATION_SELECTED, {
    variables: { insuranceSimulationId: insuranceSimulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { rcaQuotationSelected }
      }
    }) => {
      const { selectedInsuranceQuotationId } = rcaQuotationSelected;
      resetLens(prevState => ({ ...prevState, selectedInsuranceQuotationId }));
      addToast(t('Notifications.' + INSURANCE_QUOTATION_SELECTED_EVENT), 'info');
      onEndCall();
      rcaSelectionInProgressVar(false);
      rcaSelectedQuotationVar({
        id: selectedInsuranceQuotationId
      });
    }
  });

  // #endregion

  return (
    <>
      <Prompt when={isLensDirty} message={() => t('SaveChanges', { card: insuranceSimulation.insuranceTypeName })} />
      <Card title={t('Offer.Insurance.GeneralData')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id='rcaInsurance_county'
              fullWidth
              cache={false}
              value={county}
              error={!isValid(validation.countyId)}
              helperText={getErrors(validation.countyId)}
              onChange={county => {
                (lens.countyId |> set)(county?.id);
                (lens.countyName |> set)(county?.name);
                (lens.cityId |> set)(null);
                (lens.cityName |> set)(null);
              }}
              label={t('Offer.Insurance.County')}
              placeholder={t('Offer.Insurance.County')}
              valueKey='id'
              labelKey='name'
              name='county'
              options={counties}
              disabled={isReadOnly() || Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress?.districtId)}
              isSearchable={true}
              isClearable={true}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id='rcaInsurance_city'
              fullWidth
              cache={false}
              value={city}
              error={!isValid(validation.cityId)}
              helperText={getErrors(validation.cityId)}
              onChange={city => {
                (lens.cityId |> set)(city?.id);
                (lens.cityName |> set)(city?.name);
              }}
              label={t('Offer.Insurance.City')}
              placeholder={t('Offer.Insurance.City')}
              valueKey='id'
              labelKey='name'
              name='city'
              loadOptions={internalLoadCities}
              disabled={isReadOnly() || Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress?.cityId)}
              isSearchable={true}
              isClearable={true}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              fullWidth
              label={t('Offer.Insurance.Street')}
              value={(lens.street |> get) || emptyString}
              onChange={lens.street |> set}
              disabled={isReadOnly() || Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress?.street)}
              error={!isValid(validation.street)}
              helperText={getErrors(validation.street)}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <TextField
              fullWidth
              label={t('Offer.Insurance.StreetNumber')}
              value={(lens.streetNumber |> get) || emptyString}
              onChange={lens.streetNumber |> set}
              disabled={isReadOnly() || Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress?.streetNumber)}
              error={!isValid(validation.streetNumber)}
              helperText={getErrors(validation.streetNumber)}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={1}>
            <TextField
              fullWidth
              label={t('Offer.Insurance.ZipCode')}
              value={(lens.zipCode |> get) || emptyString}
              onChange={lens.zipCode |> set}
              disabled={isReadOnly() || Boolean(data?.leasingSimulationByOffer?.insured?.socialAddress?.zipCode)}
              error={!isValid(validation.zipCode)}
              helperText={getErrors(validation.zipCode)}
            />
          </Grid>
          {R.any(x => x === (lens.partnerTypeCode |> get))([partnerType.PF, partnerType.PFA]) && (
            <Grid item xs={12} md={6} lg={3}>
              <DateTime
                disabled={isReadOnly()}
                fullWidth
                value={lens.drivingLicenseDate |> get}
                label={t('Offer.Insurance.DrivingLicenseDate')}
                onChange={lens.drivingLicenseDate |> set}
                error={!isValid(validation.drivingLicenseDate)}
                helperText={getErrors(validation.drivingLicenseDate)[0]}
                timeFormat={false}
              />
            </Grid>
          )}
          {R.any(x => x === (lens.partnerTypeCode |> get))([partnerType.PJ, partnerType.PFA]) && (
            <Grid item xs={12} md={6} lg={3}>
              <Autocomplete
                id={'rcaInsurance_caen'}
                fullWidth
                isClearable={true}
                label={t('Offer.Insurance.Caen')}
                simpleValue
                value={(lens.defaultActivityFieldId |> get) || emptyString}
                disabled={isReadOnly() || (lens.existsCaen |> get)}
                onChange={value => {
                  (lens.defaultActivityFieldId |> set)(value);
                }}
                isSearchable={true}
                valueKey='id'
                labelKey='shownValue'
                options={activityFields}
                error={!isValid(validation.defaultActivityFieldId)}
                helperText={getErrors(validation.defaultActivityFieldId)}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={'rcaInsurance_vehicleCategory'}
              fullWidth
              isClearable={true}
              label={t('Offer.Insurance.VehicleCategory')}
              simpleValue
              value={(lens.categoryCode |> get) || emptyString}
              onChange={handleChangeCategory}
              isSearchable={true}
              valueKey='code'
              labelKey='name'
              options={auxiliaryData?.assetCategories || emptyArray}
              error={!isValid(validation.categoryCode)}
              helperText={getErrors(validation.categoryCode)}
              disabled={isReadOnly()}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={'rcaInsurance_vehicleSubcategory'}
              fullWidth
              isClearable={true}
              label={t('Offer.Insurance.VehicleSubcategory')}
              simpleValue
              value={lens.subcategoryCode |> get}
              valueKey='code'
              labelKey='name'
              onChange={value => (lens.subcategoryCode |> set)(value)}
              isSearchable={true}
              options={subcategoriesByCategoryCode}
              error={!isValid(validation.subcategoryCode)}
              helperText={getErrors(validation.subcategoryCode)}
              disabled={isReadOnly()}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={'rcaInsurance_usageType'}
              fullWidth
              isClearable={true}
              label={t('Offer.Insurance.UsageType')}
              simpleValue
              value={(lens.usageTypeCode |> get) || emptyString}
              onChange={usage => (lens.usageTypeCode |> set)(usage)}
              isSearchable={true}
              valueKey='code'
              labelKey='name'
              options={auxiliaryData?.usageTypes}
              error={!isValid(validation.usageTypeCode)}
              helperText={getErrors(validation.usageTypeCode)}
              disabled={isReadOnly()}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={'rcaInsurance_insuranceDuration'}
              isMultiSelection
              withCheckboxes
              isClearable={!isReadOnly()}
              limitTags={0}
              getLimitTagsText={more => getLimitTagsText(more, 'Selection', 'Selections')}
              fullWidth
              simpleValue
              label={t('Offer.Insurance.InsuranceDuration')}
              valueKey='period'
              labelKey='period'
              value={(lens.insuranceSimulationXPeriod |> get)?.map(x => x.period)}
              options={insuranceDurations.map(x => ({ period: x }))}
              getOptionDisabled={() => isReadOnly()}
              onChange={handleInsurancePeriodChange}
              error={!isValid(validation.insuranceSimulationXPeriod)}
              helperText={getErrors(validation.insuranceSimulationXPeriod)}
            />
          </Grid>
          {shouldShowFinancedInsuranceInfo && (
            <Grid item xs={12} md={6} lg={3}>
              <FormControlLabel
                control={
                  <Switch id={'offer_insurance_financedInsurance_rca'} color='primary' disabled checked={lens.financedInsurance |> get} />
                }
                label={t('Offer.Insurance.FinancedInsurance')}
              />
            </Grid>
          )}
          {shouldShowFinancedInsuranceInfo && (
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                id={'rcaInsurance_financedPeriod'}
                fullWidth
                isNumeric
                inputProps={{
                  decimalScale: 0,
                  thousandSeparator: false
                }}
                value={(lens.financedInsurancePeriod |> get) || emptyString}
                label={t('Offer.Insurance.FinancedPeriod')}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              fullWidth
              label={t('Offer.Insurance.Insured')}
              value={(lens.insuredName |> get) || emptyString}
              disabled
              error={!isValid(validation.insuredName)}
              helperText={getErrors(validation.insuredName)}
            />
          </Grid>
          <Grid item xs={12} container alignItems='center' justifyContent='flex-end'>
            {insuranceOfferURL && !isImage && hasRightToOpenInsurance ? (
              <Grid item xs={3} container justifyContent='flex-start'>
                <Paper elevation={0}>
                  <Link href={insuranceOfferURL} underline='hover' color='blue' target='_blank' rel='noopener noreferrer'>
                    {t('Offer.Insurance.InsuranceOfferNumber', {
                      insuranceOffer: lens.externalInsuranceDocumentNumber |> get
                    })}
                  </Link>
                </Paper>
              </Grid>
            ) : (
              <> </>
            )}
            {!isImage && (
              <Grid item xs={9} container justifyContent='flex-end'>
                <Button
                  id={'rcaInsurance_ProcessInsurances'}
                  color={saving ? 'defaultNoBackground' : 'primary'}
                  onClick={handleSaveInsuranceSimulation}
                  disabled={saving || isReadOnly()}
                >
                  {saving ? t('Saving') : t('Offer.Insurance.ProcessInsurances')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

RCAInsuranceGeneralData.propTypes = {
  data: PropTypes.object,
  refetchSimulation: PropTypes.func.isRequired,
  refetchQuotations: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  counties: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,
  documentDetail: PropTypes.object,
  saving: PropTypes.bool.isRequired,
  setSaving: PropTypes.func.isRequired,
  isReadOnly: PropTypes.func.isRequired
};

const getCountiesByCountryId = (state, ownProps) => {
  const countryId = ownProps.countryId || state.offerConfig.defaultCountry.id;
  return state.clientData.counties.byCountryId[countryId]?.data || emptyArray;
};

function mapStateToProps(state, ownProps) {
  return {
    counties: getCountiesByCountryId(state, ownProps),
    config: state.offerConfig
  };
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ loadCities, loadCountries, loadCounties }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(RCAInsuranceGeneralData);
